import styled from 'styled-components';
import { TaskComponentAttributeDTO, TaskComponentDTO } from '../../../../../../api/api';
import KebabMenu from '../../../../../../components/kebab-menu/KebabMenu';
import { MenuItem } from '../../../../../../components/select/Select';
import { useConfirmationDialog } from '../../../../../../hooks/useConfirmationDialog';
import { DialogBody } from '../../../../../../stateManagement/reducers/confirmDialogReducer';
import Field from './Field';
import { useMemo, useState } from 'react';
import TextField from '../../../../../../components/text-field/TextField';
import { Clickable } from '../../TaskTypeChecklist/TaskTypeChecklist';
import { CheckmarkIcon } from '../../../../../../assets/icons/CheckmarkIcon';
import { CloseIcon } from '../../../../../../assets/icons/CloseIcon';
import Typography from '../../../../../../components/typography/Typography';
import { EditIcon } from '../../../../../../assets/icons/EditIcon';

const navigateDialogBody: DialogBody = {
  headerText: 'Er du sikker?',
  bodyText:
    'Er du sikker på at du vil slette denne sektion? Alle underliggende felter vil blive slettet, og handlingen kan ikke fortrydes.',
  declineButtonText: 'Fortryd',
  confirmButtonText: 'Bekræft'
};

interface Props extends TaskComponentDTO {
  setActiveSection: () => void;
  removeSection: () => void;
  setSection: (section: TaskComponentDTO) => void;
  swapSections: (index1: number, index2: number) => void;
  numberOfSections?: number;
}

const Section = (props: Props) => {
  const { attributes, id, title, setActiveSection, removeSection, setSection, order, numberOfSections, swapSections } =
    props;
  const { getConfirmation } = useConfirmationDialog();
  const [isEditingSectionName, setIsEditingSectionName] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);

  const removeSectionHandler = async () => {
    if (!id) return;

    const confirmation = await getConfirmation(navigateDialogBody);

    if (confirmation === 'confirm') {
      removeSection();
    }
  };

  const addAttributeHandler = () => {
    if (!id) return;
    setActiveSection();
  };

  const setAttributes = (attribute: TaskComponentAttributeDTO) => {
    setSection({
      ...props,
      attributes: attributes?.map((a) => (a.attributeId === attribute.attributeId ? attribute : a))
    });
  };

  const removeAttribute = (attribute: TaskComponentAttributeDTO) => {
    setSection({ ...props, attributes: attributes?.filter((a) => a.attributeId !== attribute.attributeId) });
  };

  const swapAttributes = (index1: number, index2: number) => {
    if (!attributes) return;
    const array = [...attributes];
    const temp = array[index1];
    array[index1] = array[index2];
    array[index2] = temp;
    const result = array.map((obj, i) => {
      return { ...obj, order: i + 1 };
    });
    setSection({ ...props, attributes: result });
  };

  const sortedAttributes = useMemo(() => {
    if (!attributes) return [];
    return [...attributes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [attributes]);

  const confirmEdit = () => {
    setSection({ ...props, title: editedTitle });
    setIsEditingSectionName(false);
  };

  const cancelEdit = () => {
    setSection({ ...props, title });
    setIsEditingSectionName(false);
  };

  return (
    <StyledSection>
      <StyledHeaderContainer>
        {!isEditingSectionName && (
          <StyledSectionHeader>
            <Typography variant="h4">{title}</Typography>
            <Clickable onClick={() => setIsEditingSectionName(true)}>
              <EditIcon size="22px" />
            </Clickable>
          </StyledSectionHeader>
        )}
        {isEditingSectionName && (
          <StyledSectionHeader>
            <TextField fullWidth value={editedTitle} label="Navn" onChange={(e) => setEditedTitle(e.target.value)} />
            <Clickable onClick={confirmEdit}>
              <CheckmarkIcon size="26px" />
            </Clickable>
            <Clickable onClick={cancelEdit}>
              <CloseIcon size="26px" />
            </Clickable>
          </StyledSectionHeader>
        )}
        <KebabMenu>
          <MenuItem onClick={addAttributeHandler}>Tilføj felter</MenuItem>
          {order !== 1 && <MenuItem onClick={() => swapSections((order ?? 0) - 2, (order ?? 0) - 1)}>Ryk op</MenuItem>}
          {order !== numberOfSections && (
            <MenuItem onClick={() => swapSections((order ?? 0) - 1, order ?? 0)}>Ryk ned</MenuItem>
          )}
          <MenuItem onClick={removeSectionHandler}>Slet sektion</MenuItem>
        </KebabMenu>
      </StyledHeaderContainer>
      {sortedAttributes?.map((attribute) => (
        <Field
          key={attribute.attributeId}
          attribute={attribute}
          setAttribute={(a) => setAttributes(a)}
          removeAttribute={() => removeAttribute(attribute)}
          swapAttributes={(index1: number, index2: number) => swapAttributes(index1, index2)}
          numberOfAttributes={attributes?.length}
        />
      ))}
    </StyledSection>
  );
};

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0px;
`;

export default Section;
